import {
  getIntakePrimaryContact,
  getIntakeRestaurantName,
  type OnboardingIntake,
} from '../../types/onboardingIntake';

export type StoreUserSortKey = 'id' | 'name' | 'email' | 'role' | 'store_name';

export const STORE_USER_SORT_KEY_TO_FIELD: Record<StoreUserSortKey, string> = {
  id: 'id',
  name: 'name',
  email: 'email',
  role: 'role',
  store_name: 'store_name',
};

export interface StoreUser {
  id: number;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  store_id?: number | null;
  store?: number | null;
  [key: string]: any;
}

export interface StoreGroupUser {
  id: number;
  store_group: number;
  store_group_name: string;
  user: number;
  user_email: string;
  role: 'manager' | 'staff';
  created_at: string;
}

export interface UserFormData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: 'Manager' | 'Owner';
  store_id: number;
}

export interface StoreGroupUserFormData {
  store_group: number;
  user: number;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  role: 'manager' | 'staff';
}

export type UserAssignmentType = 'store' | 'store_group';
export type StoreGroupUserMode = 'new' | 'existing';

export interface SelectOption {
  value: string | number;
  label: string;
}

export const EMPTY_USER_FORM: UserFormData = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'Manager',
  store_id: 0,
};

export const EMPTY_STORE_GROUP_USER_FORM: StoreGroupUserFormData = {
  store_group: 0,
  user: 0,
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  role: 'manager',
};

export const STORE_USER_ROLES: UserFormData['role'][] = ['Manager', 'Owner'];

export const STORE_GROUP_USER_ROLES: {
  value: StoreGroupUserFormData['role'];
  label: string;
}[] = [
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
];

export const storeGroupRoleLabel = (role: string | null | undefined) =>
  STORE_GROUP_USER_ROLES.find((r) => r.value === role)?.label ?? role ?? '';

export const storeUserRoleTone = (role: string | null | undefined) =>
  role === 'Owner' ? 'accent' : 'neutral';

/** Select values arrive as strings; an empty choice is stored as 0. */
export const parseSelectId = (value: string) =>
  value ? parseInt(value, 10) : 0;

export const updateUserForm = (
  form: UserFormData,
  name: string,
  value: string,
  type: string,
): UserFormData => ({
  ...form,
  [name]:
    type === 'number'
      ? parseInt(value, 10) || 0
      : name === 'store_id'
        ? value === ''
          ? 0
          : parseInt(value, 10) || 0
        : value,
});

export const storeUserName = (user: StoreUser) =>
  `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—';

export const storeUserStoreLabel = (
  user: StoreUser,
  stores: { id: number; name: string }[],
) => {
  const storeId = user.store_id || user.store;
  const storeName = stores.find((s) => s.id === storeId)?.name;
  return storeName ? `${storeName} (ID: ${storeId})` : storeId || '—';
};

export const storeGroupUserFormFrom = (
  membership: StoreGroupUser,
): StoreGroupUserFormData => ({
  store_group: membership.store_group,
  user: membership.user,
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  role: membership.role,
});

export const validateStoreGroupUserForm = (
  form: StoreGroupUserFormData,
  mode: StoreGroupUserMode,
): string | null => {
  if (!form.store_group) return 'Please select a store group';
  if (mode === 'existing' && !form.user) return 'Please select a user';
  if (
    mode === 'new' &&
    (!form.email.trim() ||
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !form.password)
  ) {
    return 'First name, last name, email, and password are required.';
  }
  return null;
};

export const storeGroupUserPayload = (
  form: StoreGroupUserFormData,
  mode: StoreGroupUserMode,
) =>
  mode === 'existing'
    ? {
        store_group: form.store_group,
        user: form.user,
        role: form.role,
      }
    : {
        store_group: form.store_group,
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
        role: form.role,
      };

export const validateEditStoreGroupUserForm = (
  form: StoreGroupUserFormData,
): string | null =>
  !form.store_group || !form.user ? 'Store group and user are required.' : null;

export const responseError = (
  data: { detail?: string; message?: string },
  fallback: string,
) => data.detail || data.message || fallback;

export const thrownErrorMessage = (err: unknown) => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'An unexpected error occurred. Please try again.';
};

export const storeUserCreatedMessage = (email: string, role: string) =>
  `User "${email}" created and assigned as ${role} successfully!`;

export const storeGroupUserCreatedMessage = (data: {
  user_email?: string;
  role?: string;
}) => `Store group user "${data.user_email}" created as ${data.role}.`;

export const intakeOptionLabel = (intake: OnboardingIntake) => {
  const contact = getIntakePrimaryContact(intake);
  return `${getIntakeRestaurantName(intake) || `Intake #${intake.id}`} (ID: ${intake.id})${
    contact.email ? ` - ${contact.email}` : ''
  }`;
};
